import React, { useState, useEffect, useContext, useMemo } from "react";
import LayoutWrapper from "../../../components/LayoutWrapper";
import HeaderBar from "../../../components/HeaderBar";
import {
  Plus,
  Search,
  Filter,
  Pencil,
  Printer,
  FileText,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { ThemeContext } from "../../../context/ThemeContext";
import getCurrentSkin from "../../../utils/getCurrentSkin";

const BASE_URL = "https://nissi-india.com/api/purReqEntry";

/* ─── Helpers ───────────────────────────────────────────────────────────── */
const toApiDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};



// NEW helper for display
const formatDisplayDate = (value) => {
  if (!value) return "—";

  // Already dd/mm/yyyy
  if (value.includes("/") && value.length <= 10) return value;

  // ISO or datetime
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;

    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return value;
  }
}; 
const PurchaseReqEnteryListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const navigate = useNavigate();
  const location = useLocation();
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const isReportMode = new URLSearchParams(location.search).has("report");

  /* ── localStorage / session ───────────────────────────────── */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchIdLS =
    localStorage.getItem("branchId") ||
    localStorage.getItem("branchid") ||
    "8";
  const branch = localStorage.getItem("branchType") || "";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("userid") ||
    "";

  const storedBranchId = parseInt(branchIdLS || "8", 10);
  const branchType =
    branch === "HO" || branch === "HEAD-OFFICE" ? "HO" : "BRANCH";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchIdLS,
      "x-branch": branch,
      "x-userid": userId,
      "x-branchtype": branchType,
    }),
    [finYearId, finYear, branchIdLS, branch, userId, branchType]
  );

  const inputClass = `w-full px-3 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-${baseColor}-500/30 focus:border-${baseColor}-500 ${
    isDark
      ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
      : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
  }`;

  const labelClass = `block text-xs font-semibold tracking-wide mb-1.5 ${
    isDark ? "text-slate-300" : "text-slate-700"
  }`;

  const getDefaultFrom = () => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().split("T")[0];
  };

  const getDefaultTo = () => new Date().toISOString().split("T")[0];

  const [branchId, setBranchId] = useState(storedBranchId);
  const [branchList, setBranchList] = useState([]);
  const [fromDate, setFromDate] = useState(getDefaultFrom());
  const [toDate, setToDate] = useState(getDefaultTo());
  const [enqNo, setEnqNo] = useState("");
  const [data, setData] = useState([]);
  const [showFilters, setShowFilters] = useState(true);

  const [canAdd] = useState(true);
  const [canPrint] = useState(true);
  const [canEdit] = useState(true);

  const [initLoading, setInitLoading] = useState(true);
  const [loading, setLoading] = useState(false);

  const PAGE_SIZE = 10;
  const [page, setPage] = useState(1);
  const totalPages = Math.ceil(data.length / PAGE_SIZE);

  const pagedData = useMemo(
    () => data.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [data, page]
  );

  const getEnquiryId = (item) => {
    const val =
      item?.fld_enquiryid ??
      item?.fld_Enquiryid ??
      item?.fld_EnquiryId ??
      item?.fld_enquiryId ??
      item?.enquiryid ??
      item?.enquiryId ??
      item?.id ??
      null;

    return val !== null && val !== undefined && String(val).trim() !== ""
      ? String(val)
      : null;
  };

  /* ── Load branches ───────────────────────────────────────── */
  useEffect(() => {
    const loadBranches = async () => {
      setInitLoading(true);
      try {
        const res = await axios.get(`${BASE_URL}/getAllBranchesForDDL`, {
          params: {
            branchtype: branchType,
            branchid: storedBranchId,
          },
          headers: commonHeaders,
        });

        const branches = Array.isArray(res.data?.branches)
          ? res.data.branches
          : [];

        setBranchList(branches);

        if (res.data?.selectedBranch) {
          setBranchId(parseInt(res.data.selectedBranch, 10));
        } else if (branchType !== "HO") {
          setBranchId(storedBranchId);
        } else if (branches.length > 0) {
          setBranchId(branches[0].fld_branchid);
        }
      } catch (err) {
        console.error("Failed to fetch branches:", err);
        toast.error("Failed to fetch branches.");
      } finally {
        setInitLoading(false);
      }
    };

    loadBranches();
  }, [branchType, storedBranchId, commonHeaders]);

  /* ── Initial search ──────────────────────────────────────── */
  useEffect(() => {
    searchEnquiries(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Search ──────────────────────────────────────────────── */
  const searchEnquiries = async (silent = false) => {
    if (!silent) {
      if (!fromDate || !toDate) {
        toast.warning("Please select both from and to dates.");
        return;
      }
      if (new Date(fromDate) > new Date(toDate)) {
        toast.warning("From Date cannot be after To Date");
        return;
      }
    }

    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/getEnquiryDetails`, {
        params: {
          branchid: branchId,
          enqno: enqNo,
          fromdate: toApiDate(fromDate),
          todate: toApiDate(toDate),
        },
        headers: commonHeaders,
      });

      const rows = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.data)
        ? res.data.data
        : [];

      setData(rows);
      setPage(1);

      if (!silent && rows.length === 0) {
        toast.info("No records found.");
      }
    } catch (err) {
      console.error("Failed to fetch enquiry details:", err);
      if (!silent) toast.error("Failed to fetch enquiry details.");
    } finally {
      setLoading(false);
    }
  };

  /* ── Clear ───────────────────────────────────────────────── */
  const clearFilters = () => {
    setFromDate(getDefaultFrom());
    setToDate(getDefaultTo());
    setEnqNo("");
    setBranchId(storedBranchId);
    setData([]);
    setPage(1);
  };

  /* ── Edit ────────────────────────────────────────────────── */
  const handleEdit = (item) => {
    const enquiryId = getEnquiryId(item);
    if (!enquiryId) {
      toast.warning("Enquiry ID not found on this record.");
      return;
    }
    navigate("/inventory/PurchaseReqEnquiry_Entry", {
      state: { enquiryid: enquiryId },
    });
  };

  /* ── Print ───────────────────────────────────────────────── */
  const handlePrint = (item) => {
    const enquiryId = getEnquiryId(item);
    if (!enquiryId) {
      toast.warning("Enquiry ID not found on this record.");
      return;
    }

    const reportUrl = `/inventory/PurReqEnquiryReport?enquiryid=${encodeURIComponent(
      enquiryId
    )}`;

    const w = window.open(
      reportUrl,
      "_blank",
      "width=800,height=600,resizable=no,scrollbars=yes,toolbar=no,status=no"
    );

    if (!w) {
      toast.warning("Popup blocked. Please allow popups for this site.");
    }
  };

  return (
    <LayoutWrapper>
      <HeaderBar
        title={
          <span className="font-dmserif text-3xl">
            {isReportMode
              ? "Purchase Requisition Enquiry Report"
              : "Purchase Requisition Enquiry Listing"}
          </span>
        }
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div className="space-y-6 mt-6">
        {/* Header Card */}
        <div
          className={`p-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black border-gray-200`
          }`}
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div
                className={`flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
              >
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">
                  Purchase Requisition Enquiries
                </h1>
                <p
                  className={`text-sm ${
                    isDark ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  {isReportMode
                    ? "View and print enquiry reports"
                    : "Search and manage purchase requisition enquiries"}
                </p>
              </div>
              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  isDark
                    ? "bg-gray-700 text-gray-300"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                {data.length} records
              </span>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                  isDark
                    ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                    : "border-slate-300 text-slate-700 hover:bg-slate-100"
                } ${
                  showFilters ? (isDark ? "bg-slate-800" : "bg-slate-100") : ""
                }`}
              >
                {showFilters ? <X size={16} /> : <Filter size={16} />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              {!isReportMode && canAdd && (
                <button
                  onClick={() =>
                    navigate("/inventory/PurchaseReqEnquiry_Entry", {
                      state: {},
                    })
                  }
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-white text-sm font-semibold rounded-xl shadow transition ${buttonClass} ${hoverClass}`}
                >
                  <Plus size={16} />
                  Add New Enquiry
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Filters Card */}
        {showFilters && (
          <div
            className={`rounded-2xl shadow-xl border overflow-hidden ${
              isDark
                ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
                : `bg-white ${currentSkin.glass} border-gray-200`
            }`}
          >
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div
                  className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                ></div>
                <h3 className="text-lg font-bold">Search Filters</h3>
              </div>
            </div>

            <div className="p-6">
              {initLoading ? (
                <div className="flex items-center justify-center py-10 gap-2 text-sm text-gray-400">
                  <span className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                  Loading filters…
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                    <div>
                      <label className={labelClass}>From Date</label>
                      <input
                        type="date"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        className={inputClass}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>To Date</label>
                      <input
                        type="date"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        className={inputClass}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Branch</label>
                      <select
                        className={inputClass}
                        value={branchId}
                        onChange={(e) => setBranchId(e.target.value)}
                        disabled={branchType !== "HO"}
                      >
                        <option value="">Select</option>
                        {branchList.map((branchRow) => (
                          <option
                            key={branchRow.fld_branchid}
                            value={branchRow.fld_branchid}
                          >
                            {branchRow.fld_branchname}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Enquiry No</label>
                      <input
                        type="text"
                        value={enqNo}
                        onChange={(e) => setEnqNo(e.target.value)}
                        className={inputClass}
                        placeholder="Enter Enquiry No"
                      />
                    </div>
                  </div>

                  <div className="flex flex-wrap justify-end gap-3 mt-6 pt-5 border-t border-slate-200 dark:border-slate-700">
                    <button
                      onClick={clearFilters}
                      className={`px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                        isDark
                          ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                          : "border-slate-300 text-slate-700 hover:bg-slate-100"
                      }`}
                    >
                      Clear Filters
                    </button>

                    <button
                      onClick={() => searchEnquiries(false)}
                      disabled={loading}
                      className={`inline-flex items-center gap-2 px-5 py-2.5 text-white text-sm font-semibold rounded-xl shadow transition ${buttonClass} ${hoverClass} disabled:opacity-50`}
                    >
                      {loading ? (
                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Search size={16} />
                      )}
                      {loading ? "Searching…" : "Search Enquiries"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Results Table Card */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div
                className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
              ></div>
              <h3 className="text-xl font-bold">Enquiry List</h3>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <span className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : data.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead
                    className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                  >
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider">
                        S.No
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider">
                        Enquiry No
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider">
                        Branch
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider">
                        Supplier
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider">
                        Status
                      </th>
                      {!isReportMode && (
                        <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider">
                          Actions
                        </th>
                      )}
                      {/* {isReportMode && canPrint && (
                        <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider">
                          Print
                        </th>
                      )} */}
                    </tr>
                  </thead>

                  <tbody
                    className={`divide-y ${
                      isDark ? "divide-gray-600" : "divide-gray-200"
                    }`}
                  >
                    {pagedData.map((item, idx) => {
                      const enquiryId = getEnquiryId(item);

                      return (
                        <tr
                          key={enquiryId || idx}
                          className={`transition ${
                            isDark
                              ? "bg-gray-800/20 hover:bg-gray-700/30"
                              : "bg-white hover:bg-gray-50"
                          }`}
                        >
                          <td className="px-4 py-3 font-medium">
                            {(page - 1) * PAGE_SIZE + idx + 1}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                                isDark
                                  ? "bg-gray-700 text-gray-200"
                                  : `bg-${baseColor}-100 text-${baseColor}-800`
                              }`}
                            >
                              {item.fld_EnquiryNo || item.fld_enquiryno || "—"}
                            </span>
                          </td>
                          <td className="px-4 py-3">
  {formatDisplayDate(item.fld_date || item.dates)}
</td>
                          <td className="px-4 py-3">
  {item.fld_branchname || item.branchname || item.BranchName || "—"}
</td>
<td className="px-4 py-3 font-medium">
  {item.fld_suppliername || item.supplier || item.SupplierName || "—"}
</td>
<td className="px-4 py-3">
  <span
    className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${
      (item.status || item.Status || "").toLowerCase() === "completed"
        ? "bg-green-100 text-green-800"
        : (item.status || item.Status || "").toLowerCase() === "pending"
        ? "bg-yellow-100 text-yellow-800"
        : (item.status || item.Status || "").toLowerCase() === "cancelled"
        ? "bg-red-100 text-red-800"
        : "bg-gray-100 text-gray-800"
    }`}
  >
    {item.status || item.Status || item.fld_status || "—"}
  </span>
</td>

                          {!isReportMode && (
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-center gap-2">
                                {canEdit && (
                                  <button
                                    onClick={() => handleEdit(item)}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-lg text-xs font-semibold transition"
                                  >
                                    <Pencil size={14} />
                                    Edit
                                  </button>
                                )}
                                {/* {canPrint && (
                                  <button
                                    onClick={() => handlePrint(item)}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-100 text-amber-700 hover:bg-amber-200 rounded-lg text-xs font-semibold transition"
                                  >
                                    <Printer size={14} />
                                    Print
                                  </button>
                                )} */}
                              </div>
                            </td>
                          )}

                          {/* {isReportMode && canPrint && (
                            <td className="px-4 py-3 text-center">
                              <button
                                onClick={() => handlePrint(item)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-100 text-amber-700 hover:bg-amber-200 rounded-lg text-xs font-semibold transition"
                              >
                                <Printer size={14} />
                                Print
                              </button>
                            </td>
                          )} */}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                  <p
                    className={`text-sm ${
                      isDark ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Showing {(page - 1) * PAGE_SIZE + 1}–
                    {Math.min(page * PAGE_SIZE, data.length)} of {data.length}{" "}
                    records
                  </p>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className={`p-2 rounded-lg border transition disabled:opacity-40 disabled:cursor-not-allowed ${
                        isDark
                          ? "border-slate-600 hover:bg-slate-800"
                          : "border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      <ChevronLeft size={16} />
                    </button>

                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                      let p;
                      if (totalPages <= 5) p = i + 1;
                      else if (page <= 3) p = i + 1;
                      else if (page >= totalPages - 2) p = totalPages - 4 + i;
                      else p = page - 2 + i;

                      return (
                        <button
                          key={p}
                          onClick={() => setPage(p)}
                          className={`w-9 h-9 rounded-lg text-sm font-medium transition ${
                            p === page
                              ? `${buttonClass} text-white shadow`
                              : isDark
                              ? "border border-slate-600 hover:bg-slate-800 text-gray-300"
                              : "border border-gray-300 hover:bg-gray-50 text-gray-600"
                          }`}
                        >
                          {p}
                        </button>
                      );
                    })}

                    <button
                      onClick={() =>
                        setPage((p) => Math.min(totalPages, p + 1))
                      }
                      disabled={page === totalPages}
                      className={`p-2 rounded-lg border transition disabled:opacity-40 disabled:cursor-not-allowed ${
                        isDark
                          ? "border-slate-600 hover:bg-slate-800"
                          : "border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-16">
              <FileText size={48} className="text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
                No Records Found
              </h3>
              <p className="text-gray-500 text-center max-w-md text-sm">
                Try adjusting your search filters or add a new purchase
                requisition enquiry.
              </p>
            </div>
          )}
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />
    </LayoutWrapper>
  );
};

export default PurchaseReqEnteryListing;